import {
  useState,
} from 'react';
import type {
  FocusEvent,
  ElementType,
  DetailedHTMLProps,
  InputHTMLAttributes,
} from 'react';
import classNames from 'classnames';
import Button from '@/ui/components/Button';
import {
  CloseIcon,
} from '@/ui/icons';

import styles from './Input.module.css';

type InputProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  roundness?: 'rounded';
  icon?: ElementType;
  invalid?: boolean;
  clear?: boolean;
  onClear?: () => void;
};

const Input = (props: InputProps) => {
  const {
    ref,
    icon,
    value,
    roundness,
    invalid = false,
    readOnly = false,
    disabled = false,
    autoFocus = false,
    className,
    onBlur,
    onFocus,
    onClear,
    ...rest
  } = props;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [focused, setFocused] = useState(autoFocus);

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const Icon = icon;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (
      disabled ||
      readOnly
    ) return;

    setFocused(true);

    if (
      onFocus
    ) onFocus(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (
      disabled ||
      readOnly
    ) return;

    setFocused(false);

    if (
      onBlur
    ) onBlur(e);
  };

  const handleClear = () => {
    if (
      disabled ||
      readOnly
    ) return;

    if (
      onClear
    ) onClear();
  };

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const inputClassName = classNames({
    [styles.input]: true,
    [styles.clear]: onClear,
    [styles.invalid]: invalid,
    [styles.focused]: focused,
    [styles.disabled]: disabled,
    [styles.readOnly]: readOnly,
    [styles[roundness]]: !!roundness,
    [className]: !!className,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={inputClassName}>
      {icon && (
        <div className={styles.icon}>
          <Icon />
        </div>
      )}

      <input
        {...rest}
        ref={ref}
        value={value}
        readOnly={readOnly}
        disabled={disabled}
        autoFocus={autoFocus}
        onBlur={handleBlur}
        onFocus={handleFocus}
        className={styles.field}
      />

      {value && onClear && (
        <Button
          kind="ghost"
          color="secondary"
          onClick={handleClear}
          roundness={roundness}
          className={styles.clearButton}
          centered
        >
          <CloseIcon />
        </Button>
      )}
    </div>
  );
};

export default Input;
export type { InputProps };
